// Shared app-level layout. Both intranet and canvas extend this to get:
//   - The common grid (auto-row header, 1fr body)
//   - Document theme + root font-size
//   - Height + margin-right transition for the shared AppAssistant drawer
//     (value `H` matches AppAssistant's default width)
//
// Path-sync (publishing `window.location.pathname` / `.search` onto
// `root.currentPath` / `root.currentSearch`) is exposed as a HELPER
// function `installAppShellSync(el, s)` rather than a lifecycle hook.
// DOMQL's extends-merge replaces functions entirely, so an app that
// overrides `onCreate` on its extending shell would silently drop a
// base-level `onCreate`. Consumers invoke the helper from their own
// `onCreate` — explicit, no surprises.
//
// Consumers add their own overlays, modals, drawers as root children.
// The header slot is intentionally undeclared here: intranet mounts
// `IntranetAppHeader` and canvas mounts `CanvasAppHeader`, both of which
// extend the shared `AppHeader` base.

// The root app component in smbls does NOT inherit properties via
// `extends:` the same way children do — merging into the smblsapp root
// is handled differently. So instead of extending a component, each
// app.js imports `appShellStyles` and spreads it directly.
export const appShellStyles = {
  display: 'grid',
  gridTemplateRows: 'auto 1fr',
  gridTemplateColumns: '1fr',
  width: '100%',
  maxWidth: 'none',
  // `minHeight` (not `height`) so pages with long content grow past the
  // viewport and the viewport's own scrollbar handles scrolling. Apps
  // that need a strict viewport fit (canvas, marketplace) override with
  // `height: '100dvh'` + `overflow: 'hidden'` locally.
  minHeight: '100dvh',
  // Start with both axes visible. The framework's default can sometimes
  // land `overflow-y: auto` on the root, which turns the shell into a
  // scroll container and prevents the viewport from scrolling on long
  // pages. Apps that want to scroll inside the shell (canvas, marketplace)
  // set their own overflow explicitly.
  overflowX: 'visible',
  overflowY: 'visible',
  theme: 'document',
  transition: 'margin-right 200ms ease',
  marginRight: (el, s) => (s.root?.assistantOpen ? 'H' : '0')
}

// Installs `locationchange` patch + path-sync listeners. Call via
// `el.call('installAppShellSync', s)` — `this` is the element, `s` is
// the state slice passed in. Safe to call from any `onCreate`; the
// history patch is idempotent per window via `window.__smblsPathPatched`.
//
// Stashes the sync handler on `el._appShellSync` so the mirrored
// `uninstallAppShellSync` can detach listeners in `onRemove`.
export const installAppShellSync = function installAppShellSync(s) {
  const el = this
  const win = el.context?.window || window
  if (!win.__smblsPathPatched) {
    win.__smblsPathPatched = true
    const origPush = win.history.pushState
    const origReplace = win.history.replaceState
    win.history.pushState = function (...args) {
      const r = origPush.apply(this, args)
      win.dispatchEvent(new Event('locationchange'))
      return r
    }
    win.history.replaceState = function (...args) {
      const r = origReplace.apply(this, args)
      win.dispatchEvent(new Event('locationchange'))
      return r
    }
  }

  const root = s?.root || s || el.state?.root || el.state
  if (!root || typeof root.update !== 'function') return null

  const sync = () => {
    const path = win.location.pathname
    const search = win.location.search
    const patch = {}
    if (root.currentPath !== path) {
      patch.currentPath = path
      // Scroll reset on route change. AppShell can be either the scroll
      // container (when an app sets `overflowY: auto` on its root) or a
      // plain grid that lets the viewport scroll — hit both so either
      // mode lands at the top on navigation.
      if (el.node && typeof el.node.scrollTo === 'function') {
        el.node.scrollTo({ top: 0 })
      }
      if (typeof win.scrollTo === 'function') {
        win.scrollTo({ top: 0 })
      }
    }
    if (root.currentSearch !== search) patch.currentSearch = search
    if (Object.keys(patch).length) root.update(patch)
  }
  el._appShellSync = sync
  if (typeof win.queueMicrotask === 'function') win.queueMicrotask(sync)
  else Promise.resolve().then(sync)
  win.addEventListener('popstate', sync)
  win.addEventListener('locationchange', sync)
  return sync
}

export const uninstallAppShellSync = function uninstallAppShellSync() {
  const el = this
  const win = el.context?.window || window
  if (el._appShellSync) {
    win.removeEventListener('popstate', el._appShellSync)
    win.removeEventListener('locationchange', el._appShellSync)
    el._appShellSync = null
  }
}
